import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RoutesConfig } from 'navigation/routes';
import {
  IConfiguratorSubPageState,
  isBuyerQuotationFlowPath,
} from 'navigation/helpers';
import { AppMode } from 'store/Common/reducers/common';
import { IRootReducerState } from 'store/store';
import { useNavigateWithQuery } from './useNavigateWithQuery';
import { useQuotationLineResolveParams } from './useQuotationLineResolveParams';
import { useTemplateConfigureParams } from './useTemplateConfigureParams';
import { armConfiguratorScrollRestore } from './useConfiguratorScrollMemory';

// Back navigation shared by the configurator sub pages (BOM, Specs). They are
// reached from the configurator wherever it is hosted, and leaving it unmounts
// the whole flow, so Back has to land the user back where they were.
export const useNavigateBackFromConfiguratorSubPage = () => {
  const navigate = useNavigateWithQuery();
  const location = useLocation();
  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );
  const isTemplateMode = !!useTemplateConfigureParams();
  const isResolvingQuotationLine = !!useQuotationLineResolveParams();
  const { configuratorPathname }: IConfiguratorSubPageState =
    location.state || {};

  return () => {
    // Only this Back earns the scroll restore - leaving a sub page any other
    // way lands the user on a form they did not scroll.
    armConfiguratorScrollRestore();

    if (isTemplateMode || isResolvingQuotationLine) {
      navigate(RoutesConfig.Configure.fullPath, {
        replace: true,
        state: location.state,
      });
      return;
    }
    switch (appMode) {
      // backFromBomPage keeps the order flow from resetting itself on remount
      case AppMode.SUPPLIER:
        navigate(RoutesConfig.SupplierNewOrder.fullPath, {
          replace: true,
          state: { backFromBomPage: true },
        });
        break;
      case AppMode.BUYER:
        // A buyer configures in both the order and the quotation flow, and the
        // sub page pathname cannot tell them apart - the configurator pathname
        // stored on the way in is what decides.
        navigate(
          isBuyerQuotationFlowPath(configuratorPathname)
            ? RoutesConfig.BuyerNewQuotation.fullPath
            : RoutesConfig.BuyerNewOrder.fullPath,
          {
            replace: true,
            state: { backFromBomPage: true },
          }
        );
        break;
      case AppMode.EXTERNAL:
        navigate(RoutesConfig.Configure.fullPath, { replace: true });
    }
  };
};
